import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  area as d3Area,
  curveCatmullRom,
  extent,
  line as d3Line,
  max as d3Max,
  scaleLinear,
  scaleTime,
} from "d3";

/**
 * Responsive timeline visualization for the Breathing page.
 * Expects `data` to be an ordered array of objects with this shape:
 * { bucketStart: Date, count: number, trend: "up" | "down" }
 */
const BreathingTimeline = ({
  data,
  scaleConfig,
  isLoading,
  emptyStateMessage,
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const lastObservedHeight = useRef(null);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const ResizeObserverCtor =
      typeof window !== "undefined" ? window.ResizeObserver : null;

    if (!ResizeObserverCtor) {
      const fallbackRect = containerRef.current.getBoundingClientRect();
      setDimensions({
        width: Math.round(fallbackRect.width),
        height: Math.round(fallbackRect.height),
      });
      return undefined;
    }

    const observer = new ResizeObserverCtor((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const { width, height } = entry.contentRect;
      const roundedWidth = Math.round(width);
      const roundedHeight = Math.round(height);
      const baselineHeight = Math.max(roundedHeight, 320);

      const resolvedHeight =
        lastObservedHeight.current !== null
          ? lastObservedHeight.current
          : baselineHeight;

      setDimensions((prev) => {
        if (prev.width === roundedWidth && prev.height === resolvedHeight) {
          return prev;
        }
        return { width: roundedWidth, height: resolvedHeight };
      });

      lastObservedHeight.current = resolvedHeight;
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const hasData = data && data.length > 0;
  const { width, height } = dimensions;

  const effectiveHeight =
    height > 0
      ? Math.max(height, 320)
      : Math.max(
          Math.round(containerRef.current?.getBoundingClientRect()?.height || 0),
          420
        );

  const chart = useMemo(() => {
    if (!hasData || width === 0 || effectiveHeight === 0) {
      return null;
    }

    const margin = {
      top: 32,
      right: 48,
      bottom: 72,
      left: 80,
    };

    const innerWidth = Math.max(width - margin.left - margin.right, 1);
    const innerHeight = Math.max(
      effectiveHeight - margin.top - margin.bottom,
      1
    );

    const { approxDurationMs, tooltipFormatter } = scaleConfig;
    const dateExtent = extent(data, (d) => d.bucketStart);
    if (!dateExtent[0] || !dateExtent[1]) {
      return null;
    }

    let [xMin, xMax] = dateExtent;
    if (xMin.getTime() === xMax.getTime()) {
      const padding = approxDurationMs || 36e5;
      xMin = new Date(xMin.getTime() - padding);
      xMax = new Date(xMax.getTime() + padding);
    }

    const xScale = scaleTime()
      .domain([xMin, xMax])
      .range([margin.left, margin.left + innerWidth]);

    const maxCount = d3Max(data, (d) => d.count) ?? 0;
    const yMax = Math.max(maxCount * 1.1, 1);
    const yScale = scaleLinear()
      .domain([0, yMax])
      .range([margin.top + innerHeight, margin.top]);

    const yTickStep = Math.max(1, Math.ceil(maxCount / 8));
    const yTicks = [];
    for (let value = 0; value <= maxCount; value += yTickStep) {
      yTicks.push(value);
    }
    if (!yTicks.includes(maxCount)) {
      yTicks.push(maxCount);
    }

    const xTicks = xScale.ticks(Math.min(8, data.length + 2));

    const lineGenerator = d3Line()
      .x((d) => xScale(d.bucketStart))
      .y((d) => yScale(d.count))
      .curve(curveCatmullRom.alpha(0.6));

    const areaGenerator = d3Area()
      .x((d) => xScale(d.bucketStart))
      .y0(() => yScale(0))
      .y1((d) => yScale(d.count))
      .curve(curveCatmullRom.alpha(0.6));

    const linePath = lineGenerator(data) ?? undefined;
    const areaPath = areaGenerator(data) ?? undefined;

    const gridColor = alpha(
      theme.palette.mode === "light"
        ? theme.palette.primary.main
        : theme.palette.common.white,
      0.14
    );

    const latestPoint = data[data.length - 1];

    return {
      margin,
      innerWidth,
      innerHeight,
      xScale,
      yScale,
      yTicks,
      xTicks,
      linePath,
      areaPath,
      tooltipFormatter,
      gridColor,
      latestPoint,
    };
  }, [hasData, width, effectiveHeight, data, scaleConfig, theme]);

  const renderEmptyState = !isLoading && !hasData;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        minHeight: { xs: 320, md: 420 },
        position: "relative",
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
      <CircularProgress size={48} />
        </Box>
      )}

      {renderEmptyState && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: { xs: 4, md: 8 },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: alpha(theme.palette.text.primary, 0.72),
              fontSize: { xs: "1.05rem", md: "1.15rem" },
              fontWeight: 500,
            }}
          >
            {emptyStateMessage}
          </Typography>
        </Box>
      )}

      {chart && (
        <svg width={width} height={effectiveHeight} role="img">
          <defs>
            <linearGradient id="breath-area-fill" x1="0" x2="0" y1="0" y2="1">
              <stop
                offset="0%"
                stopColor={alpha(theme.palette.primary.light, 0.55)}
              />
              <stop
                offset="65%"
                stopColor={alpha(theme.palette.primary.main, 0.08)}
              />
            </linearGradient>
            <linearGradient
              id="breath-line-stroke"
              x1="0"
              x2="1"
              y1="0"
              y2="0"
            >
              <stop
                offset="0%"
                stopColor={alpha(theme.palette.primary.main, 0.9)}
              />
              <stop
                offset="100%"
                stopColor={alpha(theme.palette.secondary.main, 0.9)}
              />
            </linearGradient>
          </defs>

          {/* Y-axis grid */}
          {chart.yTicks.map((tick) => {
            const y = chart.yScale(tick);
            return (
              <g key={`y-grid-${tick}`}>
                <line
                  x1={chart.margin.left}
                  x2={chart.margin.left + chart.innerWidth}
                  y1={y}
                  y2={y}
                  stroke={chart.gridColor}
                  strokeDasharray="4 6"
                />
                <text
                  x={chart.margin.left - 16}
                  y={y + 4}
                  textAnchor="end"
                  fill={alpha(theme.palette.text.primary, 0.7)}
                  fontSize={12}
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {/* X-axis grid */}
          {chart.xTicks.map((tick) => {
            const x = chart.xScale(tick);
            return (
              <g key={`x-grid-${tick.getTime()}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={chart.margin.top}
                  y2={chart.margin.top + chart.innerHeight}
                  stroke={alpha(chart.gridColor, 0.6)}
                  strokeDasharray="3 8"
                />
                <text
                  x={x}
                  y={chart.margin.top + chart.innerHeight + 32}
                  textAnchor="middle"
                  fill={alpha(theme.palette.text.primary, 0.74)}
                  fontSize={12}
                >
                  {scaleConfig.tickFormatter(tick)}
                </text>
              </g>
            );
          })}

          {/* Zero axis */}
          <line
            x1={chart.margin.left}
            x2={chart.margin.left + chart.innerWidth}
            y1={chart.yScale(0)}
            y2={chart.yScale(0)}
            stroke={alpha(theme.palette.text.primary, 0.4)}
            strokeWidth={1.2}
          />

          {/* Area under curve */}
          {chart.areaPath && (
            <path
              d={chart.areaPath}
              fill="url(#breath-area-fill)"
              stroke="none"
              opacity={0.9}
            />
          )}

          {/* Trend line */}
          {chart.linePath && (
            <path
              d={chart.linePath}
              fill="none"
              stroke="url(#breath-line-stroke)"
              strokeWidth={3}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Data points */}
          {data.map((point) => {
            const x = chart.xScale(point.bucketStart);
            const y = chart.yScale(point.count);
            const isDown = point.trend === "down";
            const fillColor = isDown
              ? theme.palette.error.main
              : theme.palette.success.main;
            return (
              <g key={point.bucketStart.getTime()}>
                <circle
                  cx={x}
                  cy={y}
                  r={9}
                  stroke={alpha(theme.palette.background.paper, 0.95)}
                  strokeWidth={3}
                  fill={fillColor}
                >
                  <title>
                    {chart.tooltipFormatter(point.bucketStart)} ·{" "}
                    {point.count} deep breaths
                  </title>
                </circle>
                <circle
                  cx={x}
                  cy={y}
                  r={5}
                  fill={alpha(theme.palette.common.white, 0.85)}
                  pointerEvents="none"
                />
              </g>
            );
          })}

          {/* Latest point marker */}
          {chart.latestPoint && (
            <g
              transform={`translate(${chart.xScale(
                chart.latestPoint.bucketStart
              )}, ${chart.yScale(chart.latestPoint.count) - 22})`}
            >
              <rect
                x={-36}
                y={-26}
                width={72}
                height={24}
                rx={12}
                fill={alpha(theme.palette.background.paper, 0.9)}
                stroke={alpha(theme.palette.primary.main, 0.4)}
                strokeWidth={1}
              />
              <text
                x={0}
                y={-10}
                textAnchor="middle"
                fill={alpha(theme.palette.text.primary, 0.9)}
                fontSize={11}
                fontWeight={600}
              >
                {chart.latestPoint.count} breaths
              </text>
            </g>
          )}
        </svg>
      )}
    </Box>
  );
};

BreathingTimeline.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      bucketStart: PropTypes.instanceOf(Date).isRequired,
      count: PropTypes.number.isRequired,
      trend: PropTypes.oneOf(["up", "down"]).isRequired,
    })
  ),
  scaleConfig: PropTypes.shape({
    approxDurationMs: PropTypes.number.isRequired,
    tickFormatter: PropTypes.func.isRequired,
    tooltipFormatter: PropTypes.func.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  emptyStateMessage: PropTypes.string,
};

BreathingTimeline.defaultProps = {
  data: [],
  isLoading: false,
  emptyStateMessage:
    "Log your first deep breath to start charting your calm timeline.",
};

export default BreathingTimeline;
